import { useIntl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { useCallback, useMemo } from 'react';
import { useClient } from '../../../ClientProvider';
import { OptionCode } from '../../../enums';
import { useLatestCycle } from '../../../hooks/useCropTracking';
import useQuery from '../../../useQuery';
import { usePlanFeat } from '../../billing';
export const useMaizy = ({ plot }) => {
    const client = useClient();
    const { t } = useIntl();
    const org = useQuery(client.orgs.get(plot.organizationId), {
        enabled: !isNil(plot.organizationId)
    }).data;
    const hasCropTrackingOptionPlotActivation = plot.activeOptions.includes(OptionCode.cropTracking);
    const { lastCrop, latestCropRequest, hasNoActiveCrop, hasNoActiveCycle } = useLatestCycle({
        plot
    });
    const hasCompatibleSeed = lastCrop?.seed.isMaizyCompatible === true;
    const hasTarget = !isNil(lastCrop?.dryMatterTarget);
    const isLastCycleComplete = !isNil(lastCrop?.lastCycle?.completedOn);
    /**
     * Fetching Mai'zy result if :
     *  We have a compatible seed
     *  We have an active crop
     *  We have an active cycle (not completed)
     */
    const isMaizyResultRequestEnabled = !hasNoActiveCrop &&
        hasCompatibleSeed &&
        !isNil(lastCrop?.lastCycle?.id) &&
        !hasNoActiveCycle &&
        hasTarget &&
        !isLastCycleComplete;
    // Fetching Maizy results
    const maizyResultRequest = useQuery(client.maizy.result({ cropId: lastCrop?.id }), {
        enabled: isMaizyResultRequestEnabled
    });
    const hasMissingConfig = !hasTarget;
    const result = maizyResultRequest.data;
    const { isError } = maizyResultRequest;
    const concreteSowingDate = lastCrop?.lastCycle?.startedAt;
    const endOfSeasonDate = lastCrop?.lastCycle?.completedOn;
    /**
     * If the cycle isn't completed we base our harvest date on the result
     * Otherwise we get the end of season date (which is supposed to be the harvest)
     */
    const concreteHarvestDate = result?.harvestRange?.[1] ?? result?.harvestRange?.[0] ?? endOfSeasonDate;
    const isPostFlowering = !isNil(result?.floweringDate);
    // Fetching cumulative sum for progress bar
    const cumulativeHistoryRequest = useQuery(client.cumulativeSum.history({
        plotId: plot.id,
        before: concreteHarvestDate,
        since: concreteSowingDate
    }), { enabled: !!concreteSowingDate && !!concreteHarvestDate && !!plot });
    const cumulativeHistory = cumulativeHistoryRequest.data;
    const yesterday = plot && moment().tz(plot.timezone).startOf('day').subtract(1, 'day');
    const yesterdayTimestamp = yesterday?.unix().toString();
    const lastDayOfCumulativeHistoryTimestamp = concreteHarvestDate &&
        moment
            .unix(parseInt(concreteHarvestDate, 10))
            .startOf('day')
            .subtract(1, 'day')
            .unix()
            .toString();
    const todayDegreesCumulativeSum = useMemo(() => yesterdayTimestamp && cumulativeHistory?.cumulativeSum[yesterdayTimestamp], [yesterdayTimestamp, cumulativeHistory?.cumulativeSum]);
    const totalDegreesCumulativeSum = useMemo(() => lastDayOfCumulativeHistoryTimestamp &&
        cumulativeHistory?.referenceCumulativeSum[lastDayOfCumulativeHistoryTimestamp], [lastDayOfCumulativeHistoryTimestamp, cumulativeHistory?.referenceCumulativeSum]);
    const isLoading = !plot || latestCropRequest.isLoading || cumulativeHistoryRequest.isLoading;
    // Fetching dry matter reports
    const dryMatterReportsRequest = useQuery(client.measurementsReports.getAllPages({
        plotId: plot.id,
        since: result?.floweringDate,
        metrics: ['DM']
    }), {
        enabled: !isNil(result) && !isNil(plot.id)
    });
    // Method to refetch all maizy related data
    const refetch = useCallback(() => {
        maizyResultRequest.refetch();
        dryMatterReportsRequest.refetch();
        latestCropRequest.refetch();
        cumulativeHistoryRequest.refetch();
    }, [maizyResultRequest, dryMatterReportsRequest, latestCropRequest, cumulativeHistoryRequest]);
    const dryMatterReports = dryMatterReportsRequest.data;
    const isMissingDryMatterTarget = hasMissingConfig || isNil(result?.dryMatterTarget);
    /**
     * Formatting the given dates into complete range sentences
     */
    const getFormattedRange = useCallback(([start, end]) => start === end
        ? t('periods.at_date', {
            date: moment
                .unix(parseInt(start, 10))
                .tz(plot.timezone)
                .format(t(`formats.date`))
        })
        : t('periods.between', {
            start: moment
                .unix(parseInt(start, 10))
                .tz(plot.timezone)
                .format(t(`formats.date_short`)),
            end: moment
                .unix(parseInt(end, 10))
                .tz(plot.timezone)
                .format(t(`formats.date`))
        }), [plot, t]);
    const feat = usePlanFeat('FEAT-FORECAST-NUMDAYS', org?.billingStatus);
    const numberOfDaysInHarvestCalendar = feat?.params || 0;
    return {
        concreteHarvestDate,
        concreteSowingDate,
        dryMatterReports,
        dryMatterReportsRequest,
        endOfSeasonDate,
        getFormattedRange,
        hasCompatibleSeed,
        hasCropTrackingOptionPlotActivation,
        hasMissingConfig,
        hasNoActiveCycle,
        isError,
        isLoading,
        isMissingDryMatterTarget,
        isPostFlowering,
        lastCrop,
        latestCropRequest,
        maizyResultRequest,
        numberOfDaysInHarvestCalendar,
        refetch,
        result,
        todayDegreesCumulativeSum,
        totalDegreesCumulativeSum
    };
};
